import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { useHoverDirty } from 'react-use';
import JBImage from 'components/essentials/JBImage';
import ToggleCTALinkButton from 'components/essentials/ToggleCTALinkButton';
import CtaLink from 'components/essentials/Cta/CtaLink';

const OneSquare = ({
  config: {
    heading,
    subText,
    imageProps,
    cta,
    textColor,
    borderColor,
    borderStyle,
    toggleInputType,
  },
}) => {
  const oneSquareRef = useRef(null);
  const isHovered = useHoverDirty(oneSquareRef);

  const textColorValue = textColor?.value ?? 'Dark';
  const borderColorValue = borderColor?.value ?? 'gray';
  const borderStyleValue = borderStyle?.value ?? 'straight';

  const hasLink = !!(
    toggleInputType &&
    toggleInputType.selected &&
    toggleInputType.selected.value?.replace(' ', '') === 'CTALink' &&
    toggleInputType.ctaLink
  );

  return (
    <div
      className={classNames(
        'flex-1 h-[490px] py-10 px-6 relative min-h-[490px] lg:min-h-[560px] lg:ml-5 first:ml-0',
        {
          'text-gray': textColorValue === 'Dark',
          'text-white': textColorValue === 'Light',
          'border-none': borderColorValue === 'none',
          'border border-gray-light6 border-solid': borderColorValue === 'gray',
          'bg-origin-border border-transparent bg-rainbow border-4 border-solid':
            borderColorValue === 'rainbow',
          'rounded-none': borderStyleValue === 'straight',
          rounded: borderStyleValue === 'rounded',
        }
      )}
      ref={oneSquareRef}
    >
      {imageProps && imageProps.src ? (
        <JBImage
          src={imageProps.src}
          sizes="46vw"
          className="h-full left-0 object-cover object-center absolute top-0 w-full z-20"
        />
      ) : null}

      {heading && heading.content && (
        <h3 className="text-2xl font-bold mb-[0.33rem] text-left md:text-center relative z-20">
          {heading.content}
        </h3>
      )}
      {subText && subText.content && (
        <div className="text-base mb-[1.2rem] text-left md:text-center relative z-20">
          {subText.content}
        </div>
      )}

      {!!toggleInputType && (
        <div className="lg:text-center relative z-20">
          <ToggleCTALinkButton
            toggleInputType={toggleInputType}
            isHovered={isHovered && hasLink}
          />
        </div>
      )}

      {!toggleInputType && cta && cta.content && cta.linkTo && (
        <div className="lg:text-center relative z-20">
          <CtaLink to={cta.linkTo} className="p-0 text-right">
            {cta.content}
          </CtaLink>
        </div>
      )}

      {hasLink && (
        <Link
          to={toggleInputType.ctaLink.linkTo}
          className="block h-full left-0 absolute top-0 w-full z-20"
          tabIndex="-1"
        />
      )}
    </div>
  );
};

OneSquare.propTypes = {
  config: PropTypes.shape({
    heading: PropTypes.object,
    subText: PropTypes.shape({
      content: PropTypes.string,
    }),
    imageProps: PropTypes.shape({
      src: PropTypes.string,
    }),
    cta: PropTypes.shape({
      content: PropTypes.string,
      linkTo: PropTypes.string,
    }),
    borderColor: PropTypes.shape({
      value: PropTypes.oneOf(['rainbow', 'gray', 'none']),
    }),
    borderStyle: PropTypes.shape({
      value: PropTypes.oneOf(['rounded', 'straight']),
    }),
    textColor: PropTypes.shape({
      value: PropTypes.oneOf(['Light', 'Dark']),
    }),
    toggleInputType: PropTypes.shape({
      selected: PropTypes.shape({
        value: PropTypes.string,
      }),
      ctaLink: PropTypes.shape({
        linkTo: PropTypes.string,
        content: PropTypes.string,
      }),
    }),
  }),
};

export default OneSquare;
